/**
 * Cliente 360 — /clientes/:clienteId. Orquestrador da pagina de detalhe do
 * cliente, fiel ao mockup aprovado "Cliente 360".
 *
 * Estrutura: breadcrumb · HERO (.hero: avatar, nome+badges, meta, chips,
 * tags, acoes) · KPI strip (6) · TAB BAR (Visao geral · Pedidos & orcamentos ·
 * Financeiro · Historico & CRM · Documentos) · painel da aba ativa.
 *
 * Este arquivo segura TODA a logica/estado/handlers/modais (carregar cockpit,
 * editar, notas, bloquear/desativar/anonimizar, drill-down de ambiente) e
 * delega o conteudo de cada aba para componentes em ClienteDetalhes/tabs/.
 *
 * Dados de exibicao vem do endpoint unico GET /api/comercial/clientes/{id}/
 * cockpit. Indicadores sem fonte (NPS, etc.) aparecem como "—" — nao
 * inventamos dado.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiAlertOctagon,
  FiArrowLeft,
  FiClock,
  FiDollarSign,
  FiEdit2,
  FiEye,
  FiFile,
  FiGrid,
  FiInfo,
  FiLock,
  FiMail,
  FiMapPin,
  FiMessageCircle,
  FiMoreVertical,
  FiPackage,
  FiPhone,
  FiPlus,
  FiPower,
  FiRefreshCw,
  FiUnlock,
  FiUpload,
  FiUserPlus,
} from "react-icons/fi";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import {
  anonimizarCliente,
  atualizarCliente,
  bloquearCliente,
  criarNotaCliente,
  desativarCliente,
  desbloquearCliente,
  excluirNotaCliente,
  listarRepresentantes,
  obterCliente,
  obterCockpitCliente,
  obterDetalheAmbiente,
  reativarCliente,
} from "../../services/comercial";
import { possiveisDuplicados } from "../../services/crm";
import { buscarCep } from "../../services/enderecos";
import { PageContainer } from "../../styles/shared";

import AmbientePanel from "./AmbientePanel";
import {
  avatarGradiente,
  Badge,
  fmtBRLCompacto,
  prefixoHa,
  whatsappUrl,
} from "./ClienteDetalhes/cli360";
import { cidadeUf, enderecoPrincipal } from "./ClienteDetalhes/enderecosCliente";
import DocumentosTab from "./ClienteDetalhes/tabs/DocumentosTab";
import FinanceiroTab from "./ClienteDetalhes/tabs/FinanceiroTab";
import HistoricoTab from "./ClienteDetalhes/tabs/HistoricoTab";
import PedidosTab from "./ClienteDetalhes/tabs/PedidosTab";
import ContatosDoImovelSection from "./ClienteDetalhes/ContatosDoImovelSection";
import VisaoGeralTab from "./ClienteDetalhes/tabs/VisaoGeralTab";
import ClienteForm from "./Form";
import {
  DangerButton,
  EmptyMsg,
  ErrorMsg,
  Form,
  FormFooter,
  InfoMsg,
  InlineHint,
  PrimaryButton,
  SecondaryButton,
  Textarea,
} from "./crmStyles";

// ---------- Helpers ----------

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(str) {
  return typeof str === "string" && UUID_REGEX.test(str.trim());
}

function humanizarSlug(slug) {
  if (!slug || typeof slug !== "string") return "";
  return slug
    .split("-")
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");
}

const TIER_LABEL = {
  saudavel: "Saudável",
  atencao: "Atenção",
  em_risco: "Em risco",
  sem_score: "Sem histórico",
};
const TIER_TONE = {
  saudavel: "success",
  atencao: "warning",
  em_risco: "danger",
  sem_score: "neutral",
};

function rotuloMargem(margem) {
  if (margem == null) return "sem dados";
  if (margem >= 35) return "excelente";
  if (margem >= 25) return "boa";
  if (margem >= 15) return "ok";
  return "baixa";
}

function npsLabel(nps) {
  if (nps == null) return "sem pesquisa";
  if (nps >= 9) return "Promotor";
  if (nps >= 7) return "Neutro";
  return "Detrator";
}

function anoDe(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).getFullYear();
  } catch {
    return null;
  }
}

// ---------- Sub-componentes ----------

function KebabMenu({ acoes }) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Posiciona o menu ancorado ao botao, em coordenadas de viewport (position:
  // fixed), pois ele e renderizado via portal no body — assim NUNCA e cortado
  // pelo overflow:hidden do .hero (corner-glow) nem por stacking-context algum.
  const reposicionar = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
  }, []);

  useEffect(() => {
    if (!aberto) return undefined;
    reposicionar();
    const onDoc = (e) => {
      if (!btnRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setAberto(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", reposicionar);
    window.addEventListener("scroll", reposicionar, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", reposicionar);
      window.removeEventListener("scroll", reposicionar, true);
    };
  }, [aberto, reposicionar]);

  if (!acoes.length) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="btn btn-secondary btn-icon"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label="Mais ações"
        onClick={() => setAberto((v) => !v)}
      >
        <FiMoreVertical size={16} aria-hidden="true" />
      </button>
      {aberto && pos
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "fixed",
                top: pos.top,
                right: pos.right,
                minWidth: 210,
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
                padding: 6,
                zIndex: 1000,
              }}
            >
              {acoes.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAberto(false);
                    a.onClick();
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "8px 10px",
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                    borderRadius: "var(--radius-sm)",
                    fontSize: 13,
                    color: a.danger ? "var(--danger-text)" : "var(--ink)",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--surface-2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {a.icon} {a.label}
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

// ---------- Pagina ----------

const TABS = [
  { key: "overview", label: "Visão geral", icon: <FiGrid size={15} aria-hidden="true" /> },
  { key: "pedidos", label: "Pedidos & orçamentos", icon: <FiPackage size={15} aria-hidden="true" /> },
  { key: "financeiro", label: "Financeiro", icon: <FiDollarSign size={15} aria-hidden="true" /> },
  { key: "historico", label: "Histórico & CRM", icon: <FiClock size={15} aria-hidden="true" /> },
  { key: "documentos", label: "Documentos", icon: <FiFile size={15} aria-hidden="true" /> },
];

function ClienteDetalhe() {
  const navigate = useNavigate();
  const { clienteId } = useParams();
  const location = useLocation();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const voltarPara = location.state?.from || window.history.state?.usr?.from || "/clientes";

  const [estado, setEstado] = useState({ loading: true, error: null, cockpit: null });
  const [notas, setNotas] = useState([]);
  const [tab, setTab] = useState("overview");
  const [editando, setEditando] = useState(false);
  const [clienteEdicao, setClienteEdicao] = useState(null);
  const [bloqueando, setBloqueando] = useState(false);
  const [motivoBloqueio, setMotivoBloqueio] = useState("");
  const [confirmandoAcao, setConfirmandoAcao] = useState(null);
  const [notaAberta, setNotaAberta] = useState(false);
  const [notaTexto, setNotaTexto] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [ambienteAberto, setAmbienteAberto] = useState(false);
  const [ambienteLoading, setAmbienteLoading] = useState(false);
  const [ambienteData, setAmbienteData] = useState(null);

  const podeEditar = isMaster || hasAllPermissions(["comercial:edit"]);
  const podeAnonimizar = isMaster || hasAllPermissions(["lgpd:anonimizar"]);
  const podeCriarOrcamento = isMaster || hasAllPermissions(["orcamento:create"]);
  const podeGerirAmbientes = isMaster || hasAllPermissions(["comercial:gerir_ambientes"]);

  const carregar = useCallback(async () => {
    setEstado({ loading: true, error: null, cockpit: null });

    // ID nao-UUID = slug vindo da planilha (Planilha.jsx/toRouteId). Fallback
    // amigavel sem hit no backend (evita 404 generico).
    if (!isUuid(clienteId)) {
      setEstado({
        loading: false,
        error: null,
        cockpit: null,
        naoCadastrado: true,
        nomeAproximado: humanizarSlug(clienteId),
      });
      return;
    }

    try {
      const cockpit = await obterCockpitCliente(request, clienteId, { silentError: true });
      setEstado({ loading: false, error: null, cockpit });
      setNotas(cockpit?.notas || []);
    } catch (err) {
      if (err?.status === 404) {
        setEstado({ loading: false, error: null, cockpit: null, naoEncontrado: true });
      } else {
        setEstado({
          loading: false,
          error: err?.message || "Erro ao carregar cliente.",
          cockpit: null,
        });
      }
    }
  }, [request, clienteId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const cockpit = estado.cockpit;
  const cliente = cockpit?.cliente;
  const saude = cockpit?.saude;
  const kpis = cockpit?.kpis;
  const parceria = cockpit?.parceria;
  const origem = cockpit?.origem;
  const pedidos = useMemo(() => cockpit?.pedidos || [], [cockpit]);
  const atendimentos = cockpit?.atendimentos || [];

  // Anonimizado é estado terminal (o backend responde 409 em reativar,
  // bloquear, editar e excluir): a ficha abre para consulta, mas não oferece
  // nada que mexa no cadastro que a LGPD mandou apagar (CRM-01/CRM-10).
  const anonimizado = Boolean(cliente?.esta_anonimizado);
  const podeMexerNoCadastro = podeEditar && !anonimizado;

  const badges = useMemo(() => {
    if (!cliente || !saude) return [];
    const out = [];
    if (saude.tier === "sem_score") {
      out.push({ label: "Sem histórico", tone: "neutral" });
    } else {
      out.push({ label: `${TIER_LABEL[saude.tier]} · ${saude.score}`, tone: TIER_TONE[saude.tier] });
    }
    const status = saude.status;
    if (status?.label && status.label !== "Ativo") {
      out.push({ label: status.label === "Top 10" ? "Top 10 LTV" : status.label, tone: status.tone });
    }
    if (!cliente.ativo) out.push({ label: "Inativo", tone: "neutral" });
    if (cliente.bloqueado) out.push({ label: "Bloqueado", tone: "danger" });
    if (cliente.esta_anonimizado) out.push({ label: "Anonimizado (LGPD)", tone: "neutral" });
    return out;
  }, [cliente, saude]);

  const abrirEdicao = useCallback(async () => {
    try {
      const full = await obterCliente(request, clienteId);
      setClienteEdicao(full);
      setEditando(true);
    } catch {
      /* erro tratado pelo auto-toast do request */
    }
  }, [request, clienteId]);

  const acoes = useMemo(() => {
    if (!cliente) return [];
    const out = [];
    if (podeMexerNoCadastro) {
      if (cliente.bloqueado) {
        out.push({
          label: "Desbloquear",
          icon: <FiUnlock size={14} aria-hidden="true" />,
          onClick: async () => {
            await desbloquearCliente(request, cliente.id);
            carregar();
          },
          key: "unblock",
        });
      } else if (cliente.ativo) {
        out.push({
          label: "Bloquear...",
          icon: <FiLock size={14} aria-hidden="true" />,
          onClick: () => setBloqueando(true),
          key: "block",
        });
      }
      if (cliente.ativo) {
        out.push({
          label: "Desativar",
          icon: <FiPower size={14} aria-hidden="true" />,
          onClick: () => setConfirmandoAcao("desativar"),
          danger: true,
          key: "deact",
        });
      } else {
        out.push({
          label: "Reativar",
          icon: <FiRefreshCw size={14} aria-hidden="true" />,
          onClick: () => setConfirmandoAcao("reativar"),
          key: "react",
        });
      }
    }
    if (podeAnonimizar && !anonimizado) {
      out.push({
        label: "Anonimizar (LGPD)...",
        icon: <FiAlertOctagon size={14} aria-hidden="true" />,
        onClick: () => setConfirmandoAcao("anonimizar"),
        danger: true,
        key: "anon",
      });
    }
    return out;
  }, [cliente, podeMexerNoCadastro, podeAnonimizar, anonimizado, request, carregar]);

  // Funcao estavel: o ClienteForm recarrega a lista quando ela muda.
  const carregarParceiros = useCallback(
    () => listarRepresentantes(request, { pageSize: 200 }, { silentError: true }),
    [request]
  );

  const handleSalvarEdicao = async (dados) => {
    setSalvando(true);
    try {
      // `silentError`: o form mostra o erro por campo; o toast so repetiria.
      await atualizarCliente(request, cliente.id, dados, { silentError: true });
      setEditando(false);
      setClienteEdicao(null);
      carregar();
    } finally {
      setSalvando(false);
    }
  };

  const handleConfirmarBloqueio = async () => {
    if (!motivoBloqueio.trim()) return;
    setSalvando(true);
    try {
      await bloquearCliente(request, cliente.id, motivoBloqueio);
      setBloqueando(false);
      setMotivoBloqueio("");
      carregar();
    } finally {
      setSalvando(false);
    }
  };

  const handleConfirmarAcao = async () => {
    setSalvando(true);
    try {
      if (confirmandoAcao === "desativar") await desativarCliente(request, cliente.id);
      else if (confirmandoAcao === "reativar") await reativarCliente(request, cliente.id);
      else if (confirmandoAcao === "anonimizar") await anonimizarCliente(request, cliente.id);
      carregar();
    } finally {
      setSalvando(false);
      setConfirmandoAcao(null);
    }
  };

  const handleCriarNota = async () => {
    if (!notaTexto.trim()) return;
    setSalvando(true);
    try {
      const nova = await criarNotaCliente(request, cliente.id, notaTexto.trim());
      setNotas((prev) => [nova, ...prev]);
      setNotaTexto("");
      setNotaAberta(false);
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluirNota = async (notaId) => {
    if (!window.confirm("Excluir esta nota interna?")) return;
    const anterior = notas;
    setNotas((prev) => prev.filter((n) => n.id !== notaId));
    try {
      await excluirNotaCliente(request, notaId, { silentError: true });
    } catch {
      setNotas(anterior); // rollback
    }
  };

  const abrirWhatsapp = useCallback(() => {
    const url = whatsappUrl(cliente?.telefone);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [cliente]);

  const abrirAmbiente = useCallback(
    async (ambienteId) => {
      setAmbienteAberto(true);
      setAmbienteLoading(true);
      setAmbienteData(null);
      try {
        const detalhe = await obterDetalheAmbiente(request, ambienteId);
        setAmbienteData(detalhe);
      } catch {
        /* erro tratado pelo auto-toast do request */
        setAmbienteAberto(false);
      } finally {
        setAmbienteLoading(false);
      }
    },
    [request]
  );

  const fecharAmbiente = useCallback(() => {
    setAmbienteAberto(false);
    setAmbienteData(null);
  }, []);

  // A lista de OPs antiga (/producao/ordens) foi removida no P5 — o board
  // do Painel é a entrada única de produção (F.3); o drill-down por
  // ambiente acontece lá.
  const verOpsDoAmbiente = useCallback(() => {
    navigate("/producao");
  }, [navigate]);

  // ----- Estados de carregamento / fallback -----

  if (estado.loading) {
    return (
      <PageContainer>
        <EmptyMsg>Carregando cliente...</EmptyMsg>
      </PageContainer>
    );
  }

  if (estado.naoCadastrado) {
    return (
      <PageContainer>
        <InfoMsg>
          <FiInfo size={16} aria-hidden="true" />
          <div style={{ flex: 1 }}>
            <strong>Este cliente vem da planilha</strong>
            <p style={{ margin: "4px 0 0", color: "inherit", opacity: 0.85 }}>
              <strong>{estado.nomeAproximado || clienteId}</strong> foi listado a partir da
              planilha do Gerenciador, mas ainda não está cadastrado no PharuERP. Para
              gerenciar dados completos, importe a planilha ou cadastre manualmente.
            </p>
          </div>
        </InfoMsg>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <PrimaryButton type="button" onClick={() => navigate("/administracao/importacoes")}>
            <FiUpload size={14} aria-hidden="true" /> Importar planilha
          </PrimaryButton>
          <SecondaryButton type="button" onClick={() => navigate("/clientes")}>
            <FiUserPlus size={14} aria-hidden="true" /> Cadastrar manualmente
          </SecondaryButton>
          <SecondaryButton type="button" onClick={() => navigate("/clientes/planilha")}>
            <FiArrowLeft size={14} aria-hidden="true" /> Voltar a planilha
          </SecondaryButton>
        </div>
      </PageContainer>
    );
  }

  if (estado.naoEncontrado) {
    return (
      <PageContainer>
        <InfoMsg>
          <FiInfo size={16} aria-hidden="true" />
          <div style={{ flex: 1 }}>
            <strong>Cliente não encontrado</strong>
            <p style={{ margin: "4px 0 0", color: "inherit", opacity: 0.85 }}>
              Este cliente pode ter sido excluído, pertencer a outra unidade, ou você não
              tem permissão para visualizá-lo. Desativado e anonimizado continuam abrindo.
            </p>
          </div>
        </InfoMsg>
        <SecondaryButton type="button" onClick={() => navigate("/clientes")} style={{ marginTop: 12 }}>
          <FiArrowLeft size={14} aria-hidden="true" /> Voltar a lista
        </SecondaryButton>
      </PageContainer>
    );
  }

  if (estado.error || !cliente) {
    return (
      <PageContainer>
        <ErrorMsg>{estado.error || "Cliente não encontrado."}</ErrorMsg>
        <SecondaryButton type="button" onClick={() => navigate("/clientes")}>
          Voltar para a lista
        </SecondaryButton>
      </PageContainer>
    );
  }

  // ----- Derivados do hero / KPIs -----

  const pf = (cliente.tipo || "PF") !== "PJ";
  // O cockpit so traz `enderecos` (ADR-0071): o chip mostra o principal.
  const cidadeUfPrincipal = cidadeUf(enderecoPrincipal(cliente));
  const anoCadastro = anoDe(cliente.created_at);
  const vendedorNome = cockpit?.vendedor_nome || null;
  const tags = [...(cliente.marcadores || []), ...(cliente.perfil ? [cliente.perfil] : [])].filter(Boolean);
  const waUrl = whatsappUrl(cliente.telefone);
  const ultimo = kpis?.ultimo_pedido;
  const ativos = pedidos.filter(
    (p) => !p.entregue && !(p.situacao || p.situacao_display || "").toUpperCase().includes("CANCEL")
  ).length;
  const emProducao = pedidos.filter((p) =>
    (p.situacao || p.situacao_display || "").toUpperCase().includes("PRODUC")
  ).length;

  return (
    <PageContainer>
      <div className="cli360">
        {/* Breadcrumb */}
        <div className="c-breadcrumb">
          <button type="button" onClick={() => navigate(voltarPara)}>
            Comercial
          </button>
          <span className="sep">/</span>
          <button type="button" onClick={() => navigate("/clientes")}>
            Clientes
          </button>
          <span className="sep">/</span>
          <span className="cur">{cliente.nome}</span>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="hero-avatar" style={{ background: avatarGradiente(cliente.nome) }}>
            {cliente.iniciais || "?"}
          </div>
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="flex between wrap" style={{ gap: 14, alignItems: "flex-start" }}>
              <div style={{ minWidth: 0 }}>
                <div className="hero-name">
                  {cliente.nome}
                  {badges.map((b, i) => (
                    <Badge key={i} tone={b.tone}>
                      {b.label}
                    </Badge>
                  ))}
                </div>
                <div className="hero-meta">
                  {cliente.codigo ? (
                    <>
                      <span className="mono" style={{ color: "var(--ink-2)" }}>
                        {cliente.codigo}
                      </span>
                      <span className="dot">•</span>
                    </>
                  ) : null}
                  <span>{pf ? "Pessoa física" : "Pessoa jurídica"}</span>
                  {anoCadastro ? (
                    <>
                      <span className="dot">•</span>
                      <span>Cliente desde {anoCadastro}</span>
                    </>
                  ) : null}
                  {vendedorNome ? (
                    <>
                      <span className="dot">•</span>
                      <span>Vendedor: {vendedorNome}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="hero-actions">
                {podeCriarOrcamento && !anonimizado && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() =>
                      navigate("/orcamentos", {
                        state: { clienteId: cliente.id, clienteNome: cliente.nome },
                      })
                    }
                  >
                    <FiPlus size={15} aria-hidden="true" /> Novo orçamento
                  </button>
                )}
                {waUrl && (
                  <button type="button" className="btn btn-wa" onClick={abrirWhatsapp}>
                    <FiMessageCircle size={15} aria-hidden="true" /> WhatsApp
                  </button>
                )}
                {cliente.email && (
                  <a className="btn btn-secondary" href={`mailto:${cliente.email}`}>
                    <FiMail size={15} aria-hidden="true" /> E-mail
                  </a>
                )}
                {podeMexerNoCadastro && (
                  <button type="button" className="btn btn-secondary" onClick={abrirEdicao}>
                    <FiEdit2 size={15} aria-hidden="true" /> Editar
                  </button>
                )}
                <KebabMenu acoes={acoes} />
              </div>
            </div>

            <div className="flex wrap" style={{ gap: 8, marginTop: 16 }}>
              {cliente.email && (
                <span className="chip">
                  <FiMail aria-hidden="true" /> {cliente.email}
                </span>
              )}
              {cliente.telefone && (
                <span className="chip">
                  <FiPhone aria-hidden="true" /> {cliente.telefone}
                </span>
              )}
              {cidadeUfPrincipal && (
                <span className="chip">
                  <FiMapPin aria-hidden="true" /> {cidadeUfPrincipal}
                </span>
              )}
            </div>

            {tags.length > 0 && (
              <div className="flex wrap" style={{ gap: 6, marginTop: 11 }}>
                {tags.map((t, i) => (
                  <span className="tag" key={i}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* KPI strip */}
        <div className="kpi-row" style={{ marginTop: 16 }}>
          <div className="kpi">
            <div className="kpi-label">LTV</div>
            <div className="kpi-val">{fmtBRLCompacto(kpis?.ltv)}</div>
            <div className="kpi-sub">
              {kpis?.pedidos_count ?? 0} {(kpis?.pedidos_count ?? 0) === 1 ? "pedido" : "pedidos"}
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Ticket médio</div>
            <div className="kpi-val">{fmtBRLCompacto(kpis?.ticket_medio)}</div>
            <div className="kpi-sub">
              {kpis?.ticket_medio_geral != null ? `vs ${fmtBRLCompacto(kpis.ticket_medio_geral)} geral` : "média do cliente"}
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Pedidos</div>
            <div className="kpi-val">
              {kpis?.pedidos_count ?? 0}
              {ativos > 0 ? (
                <span className="u" style={{ fontSize: 13 }}>
                  / {ativos} ativos
                </span>
              ) : null}
            </div>
            <div className="kpi-sub">
              {emProducao > 0 ? `${emProducao} em produção` : `${kpis?.ambientes_count ?? 0} ambientes`}
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Último pedido</div>
            <div className="kpi-val" style={{ fontSize: 16 }}>
              {ultimo ? prefixoHa(ultimo.rotulo) : "—"}
            </div>
            <div className="kpi-sub">
              {ultimo ? `${ultimo.numero} · ${ultimo.situacao_display}` : "sem pedidos"}
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Margem média</div>
            <div className="kpi-val">
              {kpis?.margem_media != null ? Math.round(kpis.margem_media) : "—"}
              {kpis?.margem_media != null ? <span className="u">%</span> : null}
            </div>
            <div className="kpi-sub">
              {kpis?.margem_meta != null
                ? `${rotuloMargem(kpis?.margem_media)} · meta ${Math.round(kpis.margem_meta)}%`
                : rotuloMargem(kpis?.margem_media)}
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">NPS</div>
            <div className="kpi-val">{kpis?.nps != null ? kpis.nps : "—"}</div>
            <div className="kpi-sub">{kpis?.nps != null ? npsLabel(kpis.nps) : "sem pesquisa"}</div>
          </div>
        </div>

        {cliente.bloqueado && (
          <InfoMsg style={{ marginTop: 14 }}>
            <FiLock size={14} aria-hidden="true" /> Cliente bloqueado.
          </InfoMsg>
        )}
        {anonimizado && (
          <InfoMsg style={{ marginTop: 14 }}>
            <FiEye size={14} aria-hidden="true" /> Cadastro anonimizado — os dados pessoais do
            cliente, dos leads de origem e dos contatos do imóvel foram apagados conforme a
            LGPD. O cadastro não pode ser editado, reativado nem bloqueado.
          </InfoMsg>
        )}

        {/* TAB BAR */}
        <div className="tabbar">
          {TABS.map((t) => {
            const count = t.key === "pedidos" ? kpis?.pedidos_count : null;
            return (
              <button
                key={t.key}
                type="button"
                className={`tab${tab === t.key ? " is-active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.icon}
                {t.label}
                {count ? <span className="tcount">{count}</span> : null}
              </button>
            );
          })}
        </div>

        {/* PAINEL ATIVO */}
        {tab === "overview" && (
          <VisaoGeralTab
            cliente={cliente}
            saude={saude}
            kpis={kpis}
            pedidos={pedidos}
            atendimentos={atendimentos}
            parceria={parceria}
            origem={origem}
            onGotoTab={setTab}
          />
        )}
        {/* M-TRA-7: `ClienteFinal` tinha modelo, ViewSet completo e TRÊS
            serviços de LGPD, e nenhuma tela o alcançava. Vive aqui porque
            o morador não é entidade autônoma — é do cliente. */}
        {tab === "overview" && (
          <ContatosDoImovelSection clienteId={clienteId} somenteLeitura={anonimizado} />
        )}
        {tab === "pedidos" && (
          <PedidosTab
            cockpit={cockpit}
            pedidos={pedidos}
            clienteId={clienteId}
            request={request}
            navigate={navigate}
            podeGerirAmbientes={podeGerirAmbientes && !anonimizado}
            onAbrirAmbiente={abrirAmbiente}
            onGotoTab={setTab}
          />
        )}
        {tab === "financeiro" && (
          <FinanceiroTab request={request} clienteId={clienteId} cliente={cliente} />
        )}
        {tab === "historico" && (
          <HistoricoTab
            request={request}
            clienteId={clienteId}
            cliente={cliente}
            atendimentos={atendimentos}
            notas={notas}
            podeEditar={podeMexerNoCadastro}
            navigate={navigate}
            onNovaNota={() => setNotaAberta(true)}
            onExcluirNota={handleExcluirNota}
          />
        )}
        {tab === "documentos" && <DocumentosTab request={request} clienteId={clienteId} />}

        {/* SlideOverPanel — editar */}
        <SlideOverPanel
          open={editando}
          onClose={() => {
            setEditando(false);
            setClienteEdicao(null);
          }}
          title="Editar cliente"
          subtitle={cliente.nome}
          width="min(95vw, 640px)"
        >
          {clienteEdicao && (
            <ClienteForm
              cliente={clienteEdicao}
              loading={salvando}
              buscarCep={(cep) => buscarCep(request, cep)}
              carregarParceiros={carregarParceiros}
              consultarContatoExistente={(params) => possiveisDuplicados(request, params)}
              onSubmit={handleSalvarEdicao}
              onCancel={() => {
                setEditando(false);
                setClienteEdicao(null);
              }}
            />
          )}
        </SlideOverPanel>

        {/* SlideOverPanel — nova nota interna */}
        <SlideOverPanel
          open={notaAberta}
          onClose={() => setNotaAberta(false)}
          title="Nova nota interna"
          subtitle="Visível apenas para a equipe — não aparece para o cliente."
          width="min(90vw, 460px)"
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleCriarNota();
            }}
          >
            <Textarea
              value={notaTexto}
              onChange={(e) => setNotaTexto(e.target.value)}
              placeholder="Ex.: prefere MDF Carvalho; esposa é a decisora; reuniões por WhatsApp vídeo."
              required
              rows={4}
              autoFocus
            />
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setNotaAberta(false)} disabled={salvando}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={salvando || !notaTexto.trim()}>
                {salvando ? "Salvando..." : "Adicionar nota"}
              </PrimaryButton>
            </FormFooter>
          </Form>
        </SlideOverPanel>

        {/* SlideOverPanel — bloquear (precisa motivo) */}
        <SlideOverPanel
          open={bloqueando}
          onClose={() => setBloqueando(false)}
          title="Bloquear cliente"
          subtitle="O motivo é registrado na auditoria."
          width="min(90vw, 460px)"
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmarBloqueio();
            }}
          >
            <Textarea
              value={motivoBloqueio}
              onChange={(e) => setMotivoBloqueio(e.target.value)}
              placeholder="Ex.: inadimplência há 60 dias, fraude confirmada, etc."
              required
              rows={4}
              autoFocus
            />
            <InlineHint>Motivo é obrigatório.</InlineHint>
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setBloqueando(false)} disabled={salvando}>
                Cancelar
              </SecondaryButton>
              <DangerButton type="submit" disabled={salvando || !motivoBloqueio.trim()}>
                {salvando ? "Bloqueando..." : "Bloquear cliente"}
              </DangerButton>
            </FormFooter>
          </Form>
        </SlideOverPanel>

        {/* SlideOverPanel — confirmar desativar/reativar/anonimizar */}
        <SlideOverPanel
          open={Boolean(confirmandoAcao)}
          onClose={() => setConfirmandoAcao(null)}
          title={
            confirmandoAcao === "desativar"
              ? "Desativar cliente"
              : confirmandoAcao === "reativar"
              ? "Reativar cliente"
              : "Anonimizar cliente (LGPD)"
          }
          width="min(90vw, 460px)"
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmarAcao();
            }}
          >
            <div style={{ fontSize: "0.9rem", lineHeight: 1.5 }}>
              {confirmandoAcao === "desativar" && (
                <>
                  O cliente será marcado como <strong>inativo</strong>. Pode ser reativado depois.
                  Dados permanecem preservados.
                </>
              )}
              {confirmandoAcao === "reativar" && (
                <>O cliente voltará a aparecer nas listas e relatórios padrão.</>
              )}
              {confirmandoAcao === "anonimizar" && (
                <>
                  <strong>Atenção:</strong> esta operação apaga <strong>permanentemente</strong> os
                  dados pessoais deste cliente conforme a LGPD: nome, documento, e-mail,
                  telefone, RG, nascimento e endereços; os contatos do imóvel; as notas
                  internas; e, nos leads que viraram este cliente, contato, endereços, anexos,
                  observações e o texto dos atendimentos. Os registros de auditoria ficam sem
                  o dado pessoal. Ação <strong>irreversível</strong>: depois dela o cadastro não
                  pode ser editado, reativado nem bloqueado — permanece como esqueleto anônimo
                  para preservar os pedidos históricos.
                </>
              )}
            </div>
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setConfirmandoAcao(null)} disabled={salvando}>
                Cancelar
              </SecondaryButton>
              {confirmandoAcao === "anonimizar" || confirmandoAcao === "desativar" ? (
                <DangerButton type="submit" disabled={salvando}>
                  {salvando ? "Aplicando..." : "Confirmar"}
                </DangerButton>
              ) : (
                <PrimaryButton type="submit" disabled={salvando}>
                  {salvando ? "Aplicando..." : "Confirmar"}
                </PrimaryButton>
              )}
            </FormFooter>
          </Form>
        </SlideOverPanel>

        {/* Drill-down de ambiente (BOM CAD + status de produção) */}
        <AmbientePanel
          open={ambienteAberto}
          loading={ambienteLoading}
          data={ambienteData}
          onClose={fecharAmbiente}
          onVerOps={verOpsDoAmbiente}
        />
      </div>
    </PageContainer>
  );
}

export default ClienteDetalhe;
